import { useState, useEffect } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import api from '@/lib/api';
import { toast } from 'sonner';
import { ClipboardList, Pencil, Trash2, BarChart2, Plus, Play, ChevronRight, ChevronLeft } from 'lucide-react';
import { useIsDemo } from '@/context/DemoContext';
import { useDemoGuard } from '@/components/DemoGuard';
import { calcFourFactors, generateTeamAnalysis } from '@/lib/teamAnalysis';
import GameBreakdownModal from '@/components/GameBreakdownModal';

const C = {
  navy: '#0D1B26',
  muted: '#8FA3B1',
  border: '#DDE5EC',
  orange: '#F5A623',
  trackGame: '#CC2A2A',
  red: '#CE1141',
};

const PAGE_SIZE = 5;

export default function GamesList() {
  const [games, setGames] = useState([]);
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editGame, setEditGame] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleteGame, setDeleteGame] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [analysisModal, setAnalysisModal] = useState(null);
  const [page, setPage] = useState(0);
  const navigate = useNavigate();
  const isDemo = useIsDemo();
  const demoBlocked = useDemoGuard();

  const ctx = useOutletContext() || {};
  const { teams = [], selectedTeamId, setSelectedTeamId, isStaff } = ctx;
  const teamId = selectedTeamId;

  useEffect(() => {
    if (teamId) fetchData();
    else setLoading(false);
  }, [teamId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [gamesRes, recordRes] = await Promise.all([
        api.get(`/teams/${teamId}/games`),
        api.get(`/teams/${teamId}/record`).catch(() => ({ data: null })),
      ]);
      const sortedGames = gamesRes.data.sort(
        (a, b) => new Date(b.date_time) - new Date(a.date_time)
      );
      setGames(sortedGames);
      setRecord(recordRes.data);
      setPage(0);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load games');
    } finally {
      setLoading(false);
    }
  };

  const openEdit = (game, e) => {
    e.stopPropagation();
    if (isDemo) { demoBlocked('edit game details'); return; }
    setEditForm({
      opponent_name: game.opponent_name || '',
      date_time: game.date_time ? game.date_time.slice(0, 16) : '',
      is_home: game.is_home ? 'true' : 'false',
      home_score: game.home_score ?? 0,
      away_score: game.away_score ?? 0,
    });
    setEditGame(game);
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editGame) return;
    setSaving(true);
    try {
      await api.put(`/games/${editGame.game_id}`, {
        opponent_name: editForm.opponent_name,
        date_time: editForm.date_time,
        is_home: editForm.is_home === 'true',
        home_score: parseInt(editForm.home_score) || 0,
        away_score: parseInt(editForm.away_score) || 0,
      });
      toast.success('Game updated');
      setEditGame(null);
      fetchData();
    } catch {
      toast.error('Failed to update game');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteGame) return;
    setDeleting(true);
    try {
      await api.delete(`/games/${deleteGame.game_id}`);
      toast.success(`Game vs ${deleteGame.opponent_name} deleted`);
      setDeleteGame(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete game');
    } finally {
      setDeleting(false);
    }
  };

  const openAnalysis = async (game) => {
    setAnalysisModal({ game, analysis: null, loading: true });
    try {
      const res = await api.get(`/games/${game.game_id}/box-scores`);
      const bs = res.data;
      const homeFourFactors = calcFourFactors(bs.home.totals, bs.away.totals);
      const awayFourFactors = calcFourFactors(bs.away.totals, bs.home.totals);
      const analysis = generateTeamAnalysis(bs.home.totals, bs.away.totals, homeFourFactors, awayFourFactors);
      setAnalysisModal({ game, analysis, loading: false });
    } catch {
      setAnalysisModal({ game, analysis: null, loading: false });
    }
  };

  const GameRow = ({ game }) => {
    const isCompleted = game.status === 'final' || game.status === 'locked';
    const isLive = game.status === 'in_progress';
    const isScheduled = game.status === 'scheduled';
    const isWin = isCompleted && (
      (game.is_home && game.home_score > game.away_score) ||
      (!game.is_home && game.away_score > game.home_score)
    );
    const teamScore = game.is_home ? game.home_score : game.away_score;
    const oppScore = game.is_home ? game.away_score : game.home_score;

    // Whole row taps to the most relevant destination per status.
    const handleRowClick = () => {
      if (isCompleted) navigate(`/game/${game.game_id}/summary`);
      else if (isLive) navigate(`/game/${game.game_id}`);
      // Scheduled rows: no-op (Track Game CTA handles new sessions).
    };

    const IconBtn = ({ onClick, icon: Icon, label, testid, color, bg, disabled }) => (
      <button
        onClick={(e) => { e.stopPropagation(); if (!disabled) onClick(); }}
        disabled={disabled}
        aria-label={label}
        data-testid={testid}
        className="w-9 h-9 rounded-full inline-flex items-center justify-center transition-all active:scale-[.94] disabled:opacity-30 disabled:active:scale-100"
        style={{ background: bg, color }}
      >
        <Icon className="w-4 h-4" />
      </button>
    );

    return (
      <button
        type="button"
        onClick={handleRowClick}
        data-testid={`game-${game.game_id}`}
        className="w-full flex items-center justify-between px-4 py-4 hover:bg-slate-50 transition-colors group text-left"
      >
        {/* LEFT — W/L (or clipboard for non-completed) + opponent + date */}
        <div className="flex items-center gap-3 flex-1 min-w-0">
          {isCompleted ? (
            <div
              className="w-10 h-10 shrink-0 rounded-lg flex items-center justify-center font-bold text-lg"
              style={{
                fontFamily: "'Barlow Condensed', sans-serif",
                background: isWin ? 'rgba(13,27,38,.08)' : 'rgba(206,17,65,.1)',
                color: isWin ? C.navy : C.red,
              }}
            >
              {isWin ? 'W' : 'L'}
            </div>
          ) : (
            <div
              className="w-10 h-10 shrink-0 rounded-lg flex items-center justify-center"
              style={{ background: 'rgba(13,27,38,.06)', color: C.navy }}
            >
              <ClipboardList className="w-5 h-5" />
            </div>
          )}
          <div className="min-w-0">
            <p className="font-semibold text-[14px] truncate" style={{ color: C.navy }}>
              vs {game.opponent_name}
              {isCompleted && (
                <span className="ml-2 text-[13px] tabular-nums" style={{ color: C.muted, fontFamily: "'Barlow Condensed', sans-serif" }}>
                  {teamScore}–{oppScore}
                </span>
              )}
              {isLive && (
                <span className="ml-2 text-[13px] tabular-nums font-bold" style={{ color: '#c97e23', fontFamily: "'Barlow Condensed', sans-serif" }}>
                  {teamScore}–{oppScore}
                </span>
              )}
            </p>
            <p className="text-xs truncate" style={{ color: C.muted }}>
              {new Date(game.date_time).toLocaleDateString('en-US', {
                weekday: 'short', month: 'short', day: 'numeric',
                ...(isCompleted ? { year: 'numeric' } : { hour: 'numeric', minute: '2-digit' })
              })} • {game.is_home ? 'Home' : 'Away'}
              {isScheduled && <span className="ml-2 font-semibold" style={{ color: C.muted }}>· Scheduled</span>}
            </p>
          </div>
        </div>

        {/* RIGHT — Resume, Analysis, chevron */}
        <div className="flex items-center gap-1.5 shrink-0 ml-2">
          <IconBtn
            icon={Play}
            label="Resume game"
            testid={`resume-btn-${game.game_id}`}
            onClick={() => navigate(`/game/${game.game_id}`)}
            color={isLive ? '#0B1824' : '#94a3b8'}
            bg={isLive ? '#ffd9a8' : 'rgba(0,0,0,0.04)'}
            disabled={!isLive}
          />
          <IconBtn
            icon={BarChart2}
            label="Game analysis"
            testid={`analysis-btn-${game.game_id}`}
            onClick={() => openAnalysis(game)}
            color={isCompleted ? '#13314b' : '#94a3b8'}
            bg={isCompleted ? '#fff4c2' : 'rgba(0,0,0,0.04)'}
            disabled={!isCompleted}
          />
          <ChevronRight className="w-5 h-5 ml-0.5" style={{ color: C.muted }} />
        </div>
      </button>
    );
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="animate-spin h-12 w-12 rounded-full" style={{ border: `4px solid ${C.navy}`, borderTopColor: 'transparent' }} />
      </div>
    );
  }

  return (
    <div>
      <h1
        className="text-2xl font-extrabold uppercase tracking-tight text-slate-900 mb-5"
        style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
        data-testid="games-title"
      >
        Games
      </h1>

      {/* Compact header row: team picker + record */}
      <div className="flex flex-wrap items-center gap-3 mb-6" data-testid="games-header-row">
        <div className="flex-1 min-w-[180px] max-w-[420px]">
          <Select value={selectedTeamId || ''} onValueChange={setSelectedTeamId}>
            <SelectTrigger
              data-testid="games-team-selector"
              className="h-11 border-[1.5px] rounded-[8px] gap-2 text-[1rem] font-bold uppercase tracking-[.03em]"
              style={{ fontFamily: "'Barlow Condensed', sans-serif", background: '#fff', borderColor: C.border, color: C.navy }}
            >
              <SelectValue placeholder="Select team..." />
            </SelectTrigger>
            <SelectContent>
              {teams.map(t => (
                <SelectItem key={t.team_id} value={t.team_id}>
                  {t.name}{t.level ? ` (${t.level})` : ''}
                </SelectItem>
              ))}
              {!isStaff && (
                <SelectItem value="create_new" className="text-blue-600 font-bold border-t">
                  <span className="flex items-center"><Plus className="w-3.5 h-3.5 mr-1.5" />Create New Team</span>
                </SelectItem>
              )}
            </SelectContent>
          </Select>
        </div>
        {record && (record.wins + record.losses) > 0 && (
          <div
            className="h-11 px-4 rounded-[8px] flex items-center gap-1.5 text-[1rem] font-bold tracking-[.04em]"
            style={{ fontFamily: "'Barlow Condensed', sans-serif", background: '#fff', border: `1.5px solid ${C.border}`, color: C.navy }}
            data-testid="games-record-badge"
          >
            <span style={{ color: C.navy }}>{record.wins}</span>
            <span style={{ color: '#94a3b8' }}>–</span>
            <span style={{ color: C.red }}>{record.losses}</span>
          </div>
        )}
      </div>

      {!teamId ? (
        <div
          className="rounded-xl p-12 text-center"
          style={{ background: '#fff', border: `1.5px solid ${C.border}` }}
        >
          <p className="text-[14px]" style={{ color: C.muted }}>Select a team to view games.</p>
        </div>
      ) : (
        <>
          {/* Single primary CTA: Track Game */}
          <button
            onClick={() => {
              if (isDemo) { demoBlocked('track a new game'); return; }
              navigate('/game/new', { state: { teamId } });
            }}
            data-testid="track-game-button"
            className="w-full flex items-center justify-center gap-2 h-14 rounded-full text-[15px] font-semibold uppercase tracking-[.07em] text-white transition-all hover:opacity-90 active:scale-[.97] mb-6"
            style={{ fontFamily: "'Barlow Condensed', sans-serif", background: '#CE1141', border: 'none' }}
          >
            <ClipboardList className="w-[16px] h-[16px]" />
            Track Game
          </button>

          {games.length === 0 ? (
            <div
              className="rounded-xl px-6 py-10 text-center"
              style={{ background: '#fff', border: `1.5px solid ${C.border}` }}
              data-testid="games-empty-state"
            >
              <p className="text-[15px]" style={{ color: C.muted }}>
                Track your first game to see it here.
              </p>
            </div>
          ) : (
            <>
              <div
                className="rounded-xl overflow-hidden"
                style={{ background: '#fff', border: `1.5px solid ${C.border}` }}
                data-testid="games-list"
              >
                <div className="divide-y" style={{ borderColor: '#F4F7FA' }}>
                  {games
                    .slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE)
                    .map(g => <GameRow key={g.game_id} game={g} />)}
                </div>
              </div>

              {/* Pagination — only renders when there are more than PAGE_SIZE games.
                  Front page hides Prev; last page hides Next. Most-recent first
                  ordering is preserved (sort happens in fetchData). */}
              {games.length > PAGE_SIZE && (() => {
                const totalPages = Math.ceil(games.length / PAGE_SIZE);
                const isFirst = page === 0;
                const isLast = page >= totalPages - 1;
                const ArrowBtn = ({ direction, onClick, testid }) => (
                  <button
                    type="button"
                    onClick={onClick}
                    data-testid={testid}
                    aria-label={direction === 'prev' ? 'Previous page' : 'Next page'}
                    className="w-11 h-11 rounded-full inline-flex items-center justify-center transition-all active:scale-[.94]"
                    style={{ background: '#fff', border: `1.5px solid ${C.border}`, color: C.navy }}
                  >
                    {direction === 'prev'
                      ? <ChevronLeft className="w-5 h-5" />
                      : <ChevronRight className="w-5 h-5" />}
                  </button>
                );
                return (
                  <div
                    className="flex items-center justify-between mt-4"
                    data-testid="games-pagination"
                  >
                    {/* Left slot: Prev (hidden on first page) */}
                    <div>
                      {!isFirst && (
                        <ArrowBtn
                          direction="prev"
                          onClick={() => setPage(p => Math.max(0, p - 1))}
                          testid="games-prev-page"
                        />
                      )}
                    </div>

                    {/* Center: "Page X of Y" — anchors the row when only one
                        arrow renders, and gives users orientation across pages. */}
                    <span
                      className="text-xs font-bold uppercase tracking-wider tabular-nums"
                      style={{ color: C.muted, fontFamily: "'Barlow Condensed', sans-serif" }}
                      data-testid="games-page-indicator"
                    >
                      Page {page + 1} of {totalPages}
                    </span>

                    {/* Right slot: Next (hidden on last page) */}
                    <div>
                      {!isLast && (
                        <ArrowBtn
                          direction="next"
                          onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                          testid="games-next-page"
                        />
                      )}
                    </div>
                  </div>
                );
              })()}
            </>
          )}
        </>
      )}

      {/* Edit Game Dialog */}
      <Dialog open={!!editGame} onOpenChange={(open) => { if (!open) setEditGame(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="edit-game-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold">Edit Game</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveEdit} className="space-y-4 pt-2">
            <div>
              <Label className="text-sm font-medium">Opponent</Label>
              <Input
                value={editForm.opponent_name || ''}
                onChange={(e) => setEditForm(f => ({ ...f, opponent_name: e.target.value }))}
                required
                data-testid="edit-game-opponent"
              />
            </div>
            <div>
              <Label className="text-sm font-medium">Date & Time</Label>
              <Input
                type="datetime-local"
                value={editForm.date_time || ''}
                onChange={(e) => setEditForm(f => ({ ...f, date_time: e.target.value }))}
                required
                data-testid="edit-game-date"
              />
            </div>
            <div>
              <Label className="text-sm font-medium">Home / Away</Label>
              <Select value={editForm.is_home} onValueChange={(v) => setEditForm(f => ({ ...f, is_home: v }))}>
                <SelectTrigger data-testid="edit-game-home-away">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="true">Home</SelectItem>
                  <SelectItem value="false">Away</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-medium">Home Score</Label>
                <Input
                  type="number" min="0"
                  value={editForm.home_score ?? ''}
                  onChange={(e) => setEditForm(f => ({ ...f, home_score: e.target.value }))}
                  data-testid="edit-game-home-score"
                />
              </div>
              <div>
                <Label className="text-sm font-medium">Away Score</Label>
                <Input
                  type="number" min="0"
                  value={editForm.away_score ?? ''}
                  onChange={(e) => setEditForm(f => ({ ...f, away_score: e.target.value }))}
                  data-testid="edit-game-away-score"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditGame(null)}>Cancel</Button>
              <Button type="submit" disabled={saving} className="text-white" style={{ background: C.navy }} data-testid="save-edit-game">
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteGame} onOpenChange={(open) => { if (!open) setDeleteGame(null); }}>
        <DialogContent className="sm:max-w-sm" data-testid="delete-game-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold" style={{ color: C.red }}>Delete Game</DialogTitle>
          </DialogHeader>
          <p className="text-sm py-2" style={{ color: C.muted }}>
            Permanently delete the game <span className="font-bold" style={{ color: C.navy }}>vs {deleteGame?.opponent_name}</span> and all its stats? This cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteGame(null)}>Cancel</Button>
            <Button
              onClick={handleDelete}
              disabled={deleting}
              className="text-white"
              style={{ background: C.red }}
              data-testid="confirm-delete-game"
            >
              {deleting ? 'Deleting...' : 'Delete Game'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Game Breakdown Modal */}
      <GameBreakdownModal
        open={!!analysisModal}
        onOpenChange={() => setAnalysisModal(null)}
        analysis={analysisModal?.analysis}
        game={analysisModal?.game}
        loading={!!analysisModal?.loading}
      />
    </div>
  );
}
